'use client';

import { Suspense, useCallback, useMemo } from 'react';
import { convertTrailToTrailListItemData, convertTrailToTrailSearchResult, TrailList } from '@alltrails/trails';
import classNames from 'classnames';
import { DynamicTrailListMap, LoadingMap, TrailListMapProvider, useLocationMap } from '@alltrails/maps';
import type Trail from '@alltrails/core/types/Trail';
import styles from './PartnerListTrailList.module.scss';
import { LandingPageVariant, TrailListBlock, TrailListItemData } from '@/types/shared';

type Props = {
  trailListBlock: TrailListBlock;
  className?: string;
};

type PartnerListTrailListWithMapProps = {
  trails: Trail[];
  trailListTrails: TrailListItemData[];
};

const PartnerListTrailListWithMap = ({ trails, trailListTrails }: PartnerListTrailListWithMapProps) => {
  const { setCardHoveredResult } = useLocationMap();

  const algoliaFormattedTrails = useMemo(() => {
    return trails.map(trail => convertTrailToTrailSearchResult(trail));
  }, [trails]);

  const onListItemHover = useCallback(
    (index?: number) => {
      if (index === undefined || !algoliaFormattedTrails) {
        setCardHoveredResult(undefined);
      } else {
        const trail = algoliaFormattedTrails[index];
        if (trail && trail._geoloc) {
          setCardHoveredResult({
            _geoloc: trail._geoloc,
            _cluster_geoloc: trail._cluster_geoloc,
            ID: String(trail.ID),
            type: trail.type
          });
        }
      }
    },
    [setCardHoveredResult, algoliaFormattedTrails]
  );

  return (
    <>
      <TrailList trails={trailListTrails} variant={LandingPageVariant.Inverse} setIndexHovered={onListItemHover} />
      {algoliaFormattedTrails && (
        <div className={styles.mapContainer}>
          <div className={styles.map}>
            <Suspense fallback={<LoadingMap />}>
              <DynamicTrailListMap isLoading={false} trailSearchResults={algoliaFormattedTrails} />
            </Suspense>
          </div>
        </div>
      )}
    </>
  );
};

const PartnerListTrailList = ({ trailListBlock, className }: Props) => {
  const { show_map: showMap, trails } = trailListBlock;

  const trailListTrails = useMemo(() => {
    return trails.map(trail => convertTrailToTrailListItemData(trail));
  }, [trails]);

  if (showMap) {
    return (
      <div className={classNames(className, styles.mapAndListContainer)}>
        <TrailListMapProvider>
          <PartnerListTrailListWithMap trails={trails} trailListTrails={trailListTrails} />
        </TrailListMapProvider>
      </div>
    );
  }

  return (
    <div className={classNames(className, styles.mapAndListContainer)}>
      <TrailList trails={trailListTrails} variant={LandingPageVariant.Inverse} />
    </div>
  );
};

export default PartnerListTrailList;
